import { ButtonType, ButtonWidget, CheckBoxWithLabelWidget, DialogType, InlineAlertType, InlineAlertWidget, TextLinkKind, TextLinkWidget } from '@tableau/widgets';
import React from 'react';
import { connect } from 'react-redux';
import ReactMarkdown = require('react-markdown');
import _ = require('underscore');

import { openReactConfirmationDialog } from '~commonInfra/dialogs/ReactConfirmationDialog';
import { ModalInstance, ReactModalService } from '~commonInfra/dialogs/ReactModalService';
import { TabDialogWidth } from '~commonInfra/dialogs/style/TabDialogWidth';
import { HelpService } from '~commonInfra/services/HelpService';
import { ToasterService, ToastType } from '~commonInfra/services/ToasterService';
import { CommonStyles } from '~commonInfra/styles/CommonStyles';
import { imgPath } from '~commonInfra/utils/ImgUrl';
import { SessionSiteSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import { Formatters, Messages } from '~src/codegen/Localize';
import { MapStateToProps } from '~src/commonInfra/utils/ConnectUtils';
import { DeleteCredentialRequest } from '~src/connectivity/serverApi/requests/credentials/DeleteCredentialRequest';
import { getSlackIntegrationsForSite } from '~src/connectivity/serverApi/requests/credentials/GetIntegrationsForSiteRequest';
import { IListCredentialModelsResult, ListCredentialModelsRequest } from '~src/connectivity/serverApi/requests/credentials/ListCredentialModelsRequest';
import { removeIntegrationFromSite } from '~src/connectivity/serverApi/requests/credentials/RemoveIntegrationFromSiteRequest';
import { AddOAuthClientParams } from '~src/connectivity/serverApi/requests/OAuth/AddOAuthClientsRequest';
import { IGetOAuthClientsResult } from '~src/connectivity/serverApi/requests/OAuth/GetOAuthClientsRequest';
import { ICredentialModel } from '~src/connectivity/serverApi/types/credentials/ICredentialModel';
import { IGetIntegrationsForSiteResponse, IntegrationContext } from '~src/connectivity/serverApi/types/credentials/IGetIntegrationsForSiteResponse';
import { IOAuthClient } from '~src/connectivity/serverApi/types/OAuth/IOAuthClient';
import { OAuthCredentialTypes } from '~src/connectivity/serverApi/types/OAuth/IOAuthCredential';
import { AddOAuthCredentialAction } from '~src/connectivity/services/AddOAuthCredentialAction';
import { IResult } from '~src/httpClient/serverApi/types/IResponse';
import WebHttpClient from '~src/httpClient/WebHttpClient';
import { getSessionManagementServiceInstance } from '~src/sessionManagement/SessionManagementService';
import { AddOAuthClientDialog, AddOAuthClientDialogProps, OAuthClientSetting } from '~src/settings/general-settings/components/sections/oauth-clients-registry/AddOAuthClientDialog';
import { addOAuthClients, deleteOAuthClients, fetchOAuthClients, renderOAuthClientListTableHeader, renderOAuthClientTableRow } from '~src/settings/general-settings/components/sections/oauth-clients-registry/OAuthClientsRegistry';
import { addOAuthClientDialogStyle } from '~src/settings/general-settings/styles/oauth-client/OAuthClientsRegistryStyles';
import { learnMoreStyle, SettingsStyles } from '~src/settings/styles/CommonStyles';

import { integrationSettingConnectButton, slackAddOauthClientButton, slackConnectivityMainHeader, slackConnectText, slackDisconnectText, slackInfoText, slackLegalConsentMessage, slackLogo, slackSettingsHeaderContainer, slackUpdateAvailableText, slackUpdateAvailableTitle } from '../styles/IntegrationsSettingsStyles';
import { sendConnectToSlackTelemetry, sendUpgradeSlackConnectionTelemetry } from './SlackIntegrationSettingsTelemetry';

export const ConnectToSlack = 'connect-to-slack';
export const LegacyVirtualRecordId = 'v1|virtual|context';

export interface SlackIntegrationSettingsState {
  connected: boolean | undefined;
  oAuthClients: OAuthClientSetting[];
  integrationInfo: ICredentialModel | IntegrationContext;
  getSlackIntegrationInfoError: boolean;
  getOauthClientError: boolean;
  termsAccepted: boolean;
}
export interface SlackIntegrationSettingsStateProps {
  siteLuid: string;
}

export type SlackIntegrationSettingsProps =  SlackIntegrationSettingsStateProps;

export class SlackIntegrationSettingsComponent extends React.Component<SlackIntegrationSettingsProps, SlackIntegrationSettingsState> {
  constructor(props: SlackIntegrationSettingsProps) {
    super(props);
    this.state = { connected: undefined, integrationInfo: undefined, oAuthClients: [], getSlackIntegrationInfoError: false, getOauthClientError: false, termsAccepted: false };
  }

  componentDidMount() {
    this.updateOAuthClients();
    this.getSlackIntegrationInfo();
  }

  /**
   * Retrieve slack integration summary (if exists) for the given site,
   * and set the 'connected' state field accordingly
   */
  getSlackIntegrationInfo() {
    if (this.isTableauOnPrem()) {
      let getIntegrationInfoResult = getSlackIntegrationSummaryOnPrem();
      getIntegrationInfoResult.then((result: IListCredentialModelsResult) => {
        this.processIntegrationInfoResult(result.errors?.length > 0, result.credentialModels);
      });
    } else {
      let getIntegrationInfoResult = getSlackIntegrationsForSite({ siteLuid: this.props.siteLuid, type: 'SLACK' });
      getIntegrationInfoResult.then((result: IGetIntegrationsForSiteResponse) => {
        this.processIntegrationInfoResult(false, result.integrations);
      }).catch(() => this.processIntegrationInfoResult(true));
    }
  }

  processIntegrationInfoResult(errors?: boolean, integrationInfoModels?: ICredentialModel[] | IntegrationContext[]) {
    if (errors) {
      this.setState({ connected: false, getSlackIntegrationInfoError: true });
    } else if (integrationInfoModels && integrationInfoModels.length > 0) {
      this.setState({ connected: true, integrationInfo: integrationInfoModels[0] });
    } else {
      this.setState({ connected: false });
    }
  }

  /**
   * Retrieve the Slack OAuth client (if exists) which has been added to this site
   */
  private updateOAuthClients() {
    let oAuthClients: IOAuthClient[];
    let clientSettings: OAuthClientSetting[];
    let errorFetchingClients: boolean;
    fetchOAuthClients().then((result: IGetOAuthClientsResult) => {
      if (result.errors) {
        errorFetchingClients = true;
      } else {
        errorFetchingClients = false;
        oAuthClients = result.oauthClients.filter(client => client.type === OAuthCredentialTypes.Slack);
        clientSettings = oAuthClients.map((v: IOAuthClient) => {
          const clientSetting: OAuthClientSetting = {
            dbClass: v.type,
            instanceUrl: '',
            oauthClientId: v.clientId,
            oauthClientSecret: '',
            oauthClientRedirectUri: '',
            id: v.id
          };
          return clientSetting;
        });
      }
    }).then(() => {
      this.setState({ oAuthClients: clientSettings, getOauthClientError: errorFetchingClients });
    });
  }

  render() {
    const imageUrl = imgPath('Slack_Integration_Logo.svg');

    return (<div>
      <div className={slackSettingsHeaderContainer}>
          <img
            data-tb-test-id={`${ConnectToSlack}-logo`}
            className={slackLogo}
            src={imageUrl}
            alt={Messages.connectToSlack_slackLogo()}
          />
          <h2 className={slackConnectivityMainHeader}>{Messages.connectToSlack_settingTitle()}</h2>
        </div>
      {this.renderSlackAppInfoSection()}
      {this.isTableauOnPrem() && this.renderOAuthSection()}
      {this.renderConnectToSlackSection()}
    </div>
    );
  }

  /**
   * General Tableau on Slack info section and learn more link
   */
  renderSlackAppInfoSection() {
    const isUpgradeAvailable = this.state.connected && this.getUpgradeRequired();
    const upgradeButton =
      <ButtonWidget testId={`${ConnectToSlack}-upgrade-button`} disabled={!this.state.termsAccepted} buttonType={ButtonType.Outline} onClick={() => this.handleUpgrade()}>
        {Messages.connectToSlack_upgradeButton()}
      </ButtonWidget>;

    return (
      <div data-tb-test-id={`${ConnectToSlack}-info-section`}>
        <h3 data-tb-test-id={`${ConnectToSlack}-description-header`} className={SettingsStyles.indent}>{Messages.connectToSlack_descriptionHeader()}</h3>
        <div className={SettingsStyles.indent}>
          {isUpgradeAvailable && (
            <InlineAlertWidget
              type={InlineAlertType.Info}
              testId={`${ConnectToSlack}-upgrade-alert`}
              button={upgradeButton}
              style={{ display: 'inline-flex', marginTop: '8px' }} >
                <div className={slackUpdateAvailableTitle}>{Messages.connectToSlack_upgradeAvailableAlert()}</div>
                <div className={slackUpdateAvailableText}>{Messages.connectToSlack_upgradeAvailableMsg()}</div>
                {this.getLegalInfo('upgrade')}
            </InlineAlertWidget>
          )}
          <div data-tb-test-id={`${ConnectToSlack}-setting-description`} className={slackInfoText}>
            {Messages.connectToSlack_settingDescription()}
          </div>
          <ReactMarkdown
            data-tb-test-id={`${ConnectToSlack}-slack-privacy-info`}
            source={Messages.connectToSlack_privacyPolicy()}
          />
          <div className={slackInfoText} data-tb-test-id={`${ConnectToSlack}-required-section`}>
            <span><strong>{Messages.connectToSlack_required()}</strong></span>
            <span data-tb-test-id={`${ConnectToSlack}-required-section-description`}>
              {this.isTableauOnPrem() ? Messages.connectToSlack_requirementDescription_onprem() : Messages.connectToSlack_requirementDescription_online()}
            </span>
            <TextLinkWidget
              testId={`${ConnectToSlack}-info-section-learn-more-link`}
              kind={TextLinkKind.Standard}
              className={learnMoreStyle}
              href={HelpService.getHelpUrlByCategory('server-slack-config')}
              target={'_blank'}
              rel={'noreferrer noopener'}>
              {Messages.connectToSlack_LearnMore()}
            </TextLinkWidget>
          </div>
        </div>
      </div>
    );
  }

  handleUpgrade() {
    const sessionManagementService = getSessionManagementServiceInstance();
    AddOAuthCredentialAction.attemptAuth(OAuthCredentialTypes.Slack, null, null, null, null, /* requiresUpgrade: */ true).then((credentialId) => {
      if (credentialId) {
        ToasterService.instance.pop({ type: ToastType.Success, text: Messages.connectToSlack_toastUpgradeSuccessMsg() });
        sendUpgradeSlackConnectionTelemetry({
          userId: sessionManagementService.sessionUserId(),
          siteLuid: sessionManagementService.sessionSiteLuid(),
          version: (this.state.integrationInfo as IntegrationContext).external?.slack?.appVersion,
        });
      }
      this.getSlackIntegrationInfo();
    });
  }

  /**
   * Includes "Add OAuth Client" logic, user instructions, and table
   */
  renderOAuthSection() {
    return (
      <div data-tb-test-id={`${ConnectToSlack}-oauth-section`} className={SettingsStyles.indent}>
        <h4 data-tb-test-id={`${ConnectToSlack}-oauth-header`}>{Messages.connectToSlack_slackOAuthClientRegistry()}</h4>
        <span data-tb-test-id={`${ConnectToSlack}-oauth-description`} className={slackInfoText}>{Messages.connectToSlack_registerSlackOAuthClient()}</span>
        <TextLinkWidget
          testId={`${ConnectToSlack}-learn-more-link`}
          className={learnMoreStyle}
          kind={TextLinkKind.Standard}
          href={HelpService.getHelpUrlByCategory('server-oauth-registry')}
          target={'_blank'}
          rel={'noreferrer noopener'}>
          {Messages.connectToSlack_LearnMore()}
        </TextLinkWidget>
        {this.getAddOAuthClientButton()}
        {this.renderOAuthClientTable()}
      </div>
    );
  }

  getLegalInfo(useCase: String) {
    return(
      <div data-tb-test-id={`${ConnectToSlack}-legal-section`}>
        <ReactMarkdown className={slackLegalConsentMessage} renderers={{paragraph: 'div'}} source={Messages.connectToSlack_salesforceTermsOfServiceContent()} />
        <CheckBoxWithLabelWidget
          checked={this.state.termsAccepted}
          handleChange={() => this.setTermsAccepted(!this.state.termsAccepted)}
          testId={`${ConnectToSlack}-${useCase}-legal-checkbox`}
          label={Messages.connectToSlack_salesforceTermsOfServiceAccept()}
          containerStyle={{marginLeft: 0}} />
      </div>
    );
  }

  getAddOAuthClientButton() {
    return <ButtonWidget
      buttonType={ButtonType.Outline}
      testId='add-oauth-client-on-site-button'
      className={slackAddOauthClientButton}
      onClick={() => this.openOAuthModal()}
      disabled={this.state && this.state.oAuthClients && this.state.oAuthClients.length > 0}
      type='button'>{Messages.addOAuthClient_action()}
    </ButtonWidget>;
  }

  handleAddOAuthClient(oauthClientSetting: OAuthClientSetting) {
    const oauthParams: AddOAuthClientParams = {
      dbClass: oauthClientSetting.dbClass,
      instanceUrl: oauthClientSetting.instanceUrl,
      clientId: oauthClientSetting.oauthClientId,
      clientSecret: oauthClientSetting.oauthClientSecret,
      redirectUri: oauthClientSetting.oauthClientRedirectUri
    };
    addOAuthClients([oauthParams]).then((result: IResult) => {
      if (result.errors) {
        ToasterService.instance.pop({ type: ToastType.Error, text: Messages.connectToSlack_addOauthClient_error() });
      } else {
        this.updateOAuthClients();
        ToasterService.instance.pop({ type: ToastType.Success, text: Messages.connectToSlack_addOauthClient_succes() });
      }
    });
  }

  openOAuthModal() {
    let modalInstance: ModalInstance;
    const dialogProps: AddOAuthClientDialogProps = {
      dismiss: () => modalInstance && modalInstance.dismiss(),
      close: (result: any) => modalInstance && modalInstance.close(result),
      addOAuthClientSubmit: (formikValues: OAuthClientSetting) => this.handleAddOAuthClient(formikValues),
      hardcodeConnectionType: OAuthCredentialTypes.Slack,
      showInstanceUrlSection: false
    };

    modalInstance = ReactModalService.open({
      type: DialogType.Uber,
      dialogContent: React.createElement(AddOAuthClientDialog, dialogProps),
      testId: 'addOAuthClientDialog',
      showCloseButton: true,
      dialogStyle: addOAuthClientDialogStyle,
      shouldNotSetDefaultWidth: true
    });

    modalInstance.result.catch((reason) => {
      if (reason === 'dismissDialog' || reason === 'cancel') {
        Promise.resolve();
      } else Promise.reject();
    });
  }

  renderOAuthClientTable() {
    const deleteAction = (id: string) => deleteOAuthClients([id]);
    const deleteClient = (client: OAuthClientSetting) => {
      let description = Messages.connectToSlack_deleteOauthClient_description();
      let actionName = Messages.connectToSlack_deleteOauthClient_actionName();
      let header = Messages.connectToSlack_deleteOauthClient_header();
      this.openDeleteConfirmationDialog(description, header, actionName, deleteAction, client.id).then((result) => {
        if (result.errors) {
          ToasterService.instance.pop({ type: ToastType.Error, text: Messages.connectToSlack_deleteOauthClient_error() });
        } else {
          this.updateOAuthClients();
          ToasterService.instance.pop({ type: ToastType.Success, text: Messages.connectToSlack_deleteOauthClient_success() });
        }
      });
    };
    let stateLoaded = this.state && this.state.oAuthClients;
    if (!stateLoaded) return;
    let displayMessage = this.state.getOauthClientError ? Messages.connectToSlack_oAuthClients_errorState() : Messages.connectToSlack_oauthTable_emptyState();
    let client = this.state.oAuthClients[0];
    let emptyState = _.isUndefined(client) || _.isNull(client);
    return (
      <table id='oauth-clients-list-table-id' className='tb-static-grid-table tb-static-grid-table-settings-width'>
        <thead>
          <tr>
            {renderOAuthClientListTableHeader(true)}
          </tr>
        </thead>
        <tbody id='oauth-clients-list-body-id'>
          {renderOAuthClientTableRow({ client, deleteClient, isSlackOAuthClient: true }, emptyState, this.state.connected, displayMessage)}
        </tbody>
      </table>
    );
  }

  /**
   * Includes the button to connect to slack, instructions, and slack credentials table
   */
  renderConnectToSlackSection() {
    let isConnectToSlackButtonDisabled = false;
    let legalTextRequired = false;
    if (!this.isTableauOnPrem() && this.state.connected === false) {
      legalTextRequired = true;
      if (!this.state.termsAccepted) {
        isConnectToSlackButtonDisabled = true;
      }
    }
    if (this.isTableauOnPrem()) {
      isConnectToSlackButtonDisabled = (this.state.oAuthClients.length === 0 && !this.state.connected);
    }

    return (
      <div data-tb-test-id={`${ConnectToSlack}-section`} className={SettingsStyles.indent}>
        {this.renderConnectToSlackHeaderAndDescription()}
        <div>
        {legalTextRequired && (
            <InlineAlertWidget
              type={InlineAlertType.Info}
              testId={`${ConnectToSlack}-connect-legal-notice`}
              style={{ display: 'inline-flex', marginBottom: '16px' }} >
                {this.getLegalInfo('connect')}
            </InlineAlertWidget>)}
          <ButtonWidget
            testId={`${ConnectToSlack}-button`}
            className={integrationSettingConnectButton}
            handleClick={() => { this.handleConnectToSlack(this.state.connected, this.state.integrationInfo && this.getIntegrationInfoLuid()); }}
            buttonType={ButtonType.Outline}
            disabled={isConnectToSlackButtonDisabled || this.state.getSlackIntegrationInfoError}
            showSpinner={this.state.connected === undefined}
          >
            {(this.state.connected) ? Messages.connectToSlack_disconnect() : Messages.connectToSlack()}
          </ButtonWidget>
        </div>
        {this.state.connected && (
          <div data-tb-test-id={`${ConnectToSlack}-disconnect-description`} className={slackDisconnectText}>
            {Messages.connectToSlack_disconnectFromSlack_description()}
          </div>
          )}
        {this.renderSlackConnectionTable()}
      </div>
    );
  }

  renderConnectToSlackHeaderAndDescription() {
    if (!this.isTableauOnPrem()) {
      // In TOL we do not display a title + description above the Slack button.
      // Add a space above the "Connect to Slack" button
      return <div data-tb-test-id={`${ConnectToSlack}-line-height-space`} style={{ height: CommonStyles.Sizes.LineHeight }}></div>;
    }

    return (
      <div>
        <h3 data-tb-test-id={`${ConnectToSlack}-header`}>{Messages.connectToSlack()}</h3>
        <div data-tb-test-id={`${ConnectToSlack}-connect-instructions`} className={slackConnectText}>{Messages.connectToSlack_connectToSlackWorkspaceDescription()}</div>
      </div>
    );
  }

  renderSlackConnectionTable() {
    let workspaceName = this.state.connected ? this.getWorkspaceName() :
      this.state.getSlackIntegrationInfoError ? Messages.connectToSlack_slackCredentials_errorState() : Messages.connectToSlack_notConnectedToWorkspace();
    let connectedOn = '';
    if (this.state.connected) {
      let dateString = isFromRest(this.state.integrationInfo) ? (this.state.integrationInfo as unknown as IntegrationContext).modifiedAt
      : this.state.integrationInfo.createdAt;
      let date: Date = new Date(dateString);
      let validDate = date && !isNaN(date.getTime());
      connectedOn = validDate ? Formatters.formatMediumDateTimeWithoutSeconds(date) : Messages.connectToSlack_connectedOn_errorState();
    }

    return (
      <table id='slack-connection-table-id' className='tb-static-grid-table tb-static-grid-table-settings-width'>
        <thead>
          <tr>
            {this.renderSlackConnectionTableHeader()}
          </tr>
        </thead>
        <tbody id='slack-connection-list-body-id'>
          <tr>
            <td><span id='slack-connection-workspace-name' className='tb-data-grid-cell-text tb-lr-padded-wide'>{workspaceName}</span></td>
            <td><span id='slack-connection-connected-on' className='tb-data-grid-cell-text tb-lr-padded-wide'>{connectedOn}</span></td>
          </tr>
        </tbody>
      </table>
    );
  }

  renderSlackConnectionTableHeader() {
    const tableHeaders = { [Messages.connectToSlack_slackWorkspace()]: '', [Messages.connectToSlack_connectedOn()]: '' };
    let header = Object.keys(tableHeaders);
    return header.map((key, index) => {
      return (<th key={index} className='tb-data-grid-headers-line'>
        <span className='tb-data-grid-header-text'>{key}</span>
      </th>);
    });
  }

  /**
   * When the user presses "Disconnect from Slack", the corresponding credential should be deleted
   * @param luid string that corresponds to the slack credential to be deleted
   * @returns Result of the delete call
   */
  disconnectFromSlack(luid: string): Promise<IResult> {
    const slackCredentialsRequest = new DeleteCredentialRequest({ luid: luid, targetClass: OAuthCredentialTypes.Slack });
    return WebHttpClient.requestPost(slackCredentialsRequest);
  }

  /**
   * We get into this situation in Online if SIP is enabled but the user has a V1/legacy connection.
   * We need to query the credential to delete and then call the delete credential API using that luid.
   * @returns Result of delete credential call.
   */
  disconnectFromSlackLegacy(): () => Promise<IResult> {
    let getIntegrationInfoResult = getSlackIntegrationSummaryOnPrem();
    return () => getIntegrationInfoResult.then(async (result: IListCredentialModelsResult) => {
        let credentials = result.credentialModels;
        return await this.disconnectFromSlack(credentials[0].luid);
      });
  }

  disconnectFromSlackRest(): () => Promise<IResult> {
    const integrationId = this.getIntegrationId();
    return () => {
      return removeIntegrationFromSite(integrationId);
    };
  }

  handleConnectToSlack(alreadyConnected: boolean, id?: string) {
    const sessionManagementService = getSessionManagementServiceInstance();
    if (alreadyConnected) {
      let description = Messages.connectToSlack_disconnectFromSlack_modalDescription();
      let actionName = Messages.connectToSlack_disconnectFromSlack_actionName();
      let header = Messages.connectToSlack_disconnectFromSlack_header();

      let disconnectAction;
      if (isFromRest(this.state.integrationInfo) && isVirtualRecord(this.state.integrationInfo)) {
        disconnectAction = this.disconnectFromSlackLegacy();
      } else if (isFromRest(this.state.integrationInfo)) {
        disconnectAction = this.disconnectFromSlackRest();
      } else {
        disconnectAction = this.disconnectFromSlack;
      }

      this.openDeleteConfirmationDialog(description, header, actionName, disconnectAction, id).then((result) => {
        if (result.errors) {
          ToasterService.instance.pop({ type: ToastType.Error, text: Messages.connectToSlack_disconnectFromSlack_error() });
        } else {
          this.setState({ connected: false });
          ToasterService.instance.pop({ type: ToastType.Success, text: Messages.connectToSlack_disconnectFromSlack_success() });
          sendConnectToSlackTelemetry({
            userId: sessionManagementService.sessionUserId(),
            siteLuid: sessionManagementService.sessionSiteLuid(),
            connectToSlack: false
          });
        }
      }).catch((reason) => {
        if (reason !== 'cancel') {
          ToasterService.instance.pop({ type: ToastType.Error, text: Messages.connectToSlack_disconnectFromSlack_error() });
        }
      });
    } else {
      AddOAuthCredentialAction.attemptAuth(OAuthCredentialTypes.Slack).then((result) => {
        if (result) {
          ToasterService.instance.pop({ type: ToastType.Success, text: Messages.connectToSlack_toastSuccessMsg() });
          sendConnectToSlackTelemetry({
            userId: sessionManagementService.sessionUserId(),
            siteLuid: sessionManagementService.sessionSiteLuid(),
            connectToSlack: true
          });
        }
        this.getSlackIntegrationInfo();
      });
    }
  }

  /**
   * A confirmation dialog used for destructive actions on this page:
   * 1. Disconnecting from slack (deleting slack credential)
   * 2. Deleting Slack OAuth client
   * @param description The body test for the dialog
   * @param header The dialog title
   * @param actionName Wording for the destructive action (e.g. "Delete")
   * @param deleteAction The handler function
   * @param id Parameter used for the destructive action (ID associated with deleted credential/OAuth client)
   * @returns Dialog result
   */
  openDeleteConfirmationDialog(description: string, header: string, actionName: string, deleteAction: (id: string) => PromiseLike<IResult>, id: string): Promise<IResult> {
    const confirmationDialog = openReactConfirmationDialog<string, IResult>({
      title: header,
      message: description,
      paramsForAction: id,
      actionName,
      isActionDelete: true,
      dialogWidth: TabDialogWidth.width375px,
      action: () => deleteAction(id)
    });
    return confirmationDialog.result;
  }

  private isTableauOnPrem() {
    const sessionManagementService = getSessionManagementServiceInstance();
    return !sessionManagementService.isOnlineEnvironment();
  }

  getIntegrationInfoLuid() {
    if (isFromRest(this.state.integrationInfo)) {
      return undefined;
    }

    return this.state.integrationInfo.luid;
  }

  getIntegrationId() {
    if (isFromRest(this.state.integrationInfo)) {
      return this.state.integrationInfo.id;
    }

    return '';
  }

  getWorkspaceName() {
    if (isFromRest(this.state.integrationInfo)) {
      return this.state.integrationInfo.external.slack.name;
    }

    return this.state.integrationInfo.target.targetEndpoint;
  }

  getUpgradeRequired() {
    if (isFromRest(this.state.integrationInfo)) {
      return this.state.integrationInfo.isUpgradeRequired;
    }

    return false;
  }

  private setTermsAccepted(isAccepted: boolean) {
    this.setState({termsAccepted: isAccepted});
  }
}

function getSlackIntegrationSummaryOnPrem() {
  const slackIntegrationInfoRequest = new ListCredentialModelsRequest({ targetClass: OAuthCredentialTypes.Slack });
  return WebHttpClient.requestPost(slackIntegrationInfoRequest);
}

/**
 * When Online, we make a rest call instead to get the
 * integration info. Since the types returned from the REST and non-REST methods are organized
 * differently, we need ot differentiate between what type was returned when getting certain
 * information.
 * 
 * @param model the credential model from state
 * @returns whether the model is type IntegrationContext, which is returned when a REST call is made
 */
function isFromRest(model: ICredentialModel | IntegrationContext): model is IntegrationContext {
  return (model as IntegrationContext).tableau !== undefined;
}

function isVirtualRecord(model: IntegrationContext) {
  return model.id === LegacyVirtualRecordId;
}

const mapStateToProps: MapStateToProps<SlackIntegrationSettingsStateProps> = (state) => ({
  siteLuid: SessionSiteSelectors.siteLuid(state),
});

export const SlackIntegrationSettings = connect(mapStateToProps)(SlackIntegrationSettingsComponent);